import { useQuery } from "@tanstack/react-query";
import {
	calculateHealthFactor,
	calculateLtv,
	getPriceFeeds,
	getVaults,
	type Vault,
} from "@motokomaker/ic-client";
import { useAuth } from "@/lib/auth";
import { getV2ProtocolActor, hasProtocolEnv } from "@/lib/protocol-actor";

export type UiVault = {
	id: bigint;
	collateralLabel: string;
	collateralUsd: number;
	debtUsdm: number;
	ltvPct: number;
	healthFactor: number;
	status: "Active" | "AtRisk" | "Finished" | "Liquidated";
	source: "live";
	/** Owner principal text when known (owned path or public owner field). */
	owner?: string | null;
	/** Vault open timestamp (ns) when known. */
	createdNs?: bigint | null;
	raw?: Vault;
	/** True when authenticated principal owns this vault. */
	isOwner?: boolean;
};

async function enrichVaults(vaults: Vault[]): Promise<UiVault[]> {
	const actor = await getV2ProtocolActor();
	const prices: Record<string, number> = {};
	if (actor) {
		try {
			const feeds = await getPriceFeeds(actor);
			for (const f of feeds) {
				prices[f.asset] = f.price;
			}
		} catch {
			/* prices optional for list; amounts still show */
		}
	}

	return vaults.map((v) => {
		const collateralUsd = v.collateral.reduce((sum, [key, amt]) => {
			const p = prices[key] ?? 0;
			return sum + amt * p;
		}, 0);
		const debtUsdm = v.debt;
		const ltvPct = calculateLtv(debtUsdm, collateralUsd);
		const hf = calculateHealthFactor(collateralUsd, debtUsdm);
		const label = v.collateral.map(([k]) => k).join(" · ") || "Empty";
		let owner: string | null = null;
		try {
			owner = v.owner.toText();
		} catch {
			owner = null;
		}
		return {
			id: v.id,
			collateralLabel: label,
			collateralUsd,
			debtUsdm,
			ltvPct,
			healthFactor: hf,
			status: ltvPct >= 79 ? "AtRisk" : "Active",
			source: "live" as const,
			owner,
			createdNs: v.created ?? null,
			raw: v,
		};
	});
}

export function useUserVaults() {
	const {
		isAuthenticated,
		actor,
		principal,
		sessionStatus,
		reportSessionIssue,
		ensureProtocolActor,
	} = useAuth();

	return useQuery({
		queryKey: [
			"v2",
			"userVaults",
			principal ?? "anon",
			sessionStatus,
			!!actor,
		],
		queryFn: async (): Promise<UiVault[]> => {
			if (!isAuthenticated) return [];
			if (!hasProtocolEnv()) return [];
			let a = actor;
			if (!a) {
				const pair = await ensureProtocolActor();
				a = pair?.actor ?? null;
			}
			if (!a) {
				// II connected but no actor — not an empty portfolio
				reportSessionIssue("degraded");
				throw new Error(
					"Protocol session unavailable — reconnect to load your vaults (not empty).",
				);
			}
			try {
				const vaults = await getVaults(a);
				const mine = vaults.filter((v) => {
					try {
						return v.owner.toText() === principal;
					} catch {
						return true;
					}
				});
				return enrichVaults(mine);
			} catch (e) {
				const msg = e instanceof Error ? e.message : String(e);
				const lower = msg.toLowerCase();
				if (
					lower.includes("unauthorized") ||
					lower.includes("user permission")
				) {
					reportSessionIssue("unauthorized");
					throw new Error(
						"Session lost protocol access — log in again to see your vaults.",
					);
				}
				throw e;
			}
		},
		enabled: isAuthenticated && hasProtocolEnv(),
		staleTime: 20_000,
		retry: 1,
	});
}

export function useVaultDetail(vaultId: string | undefined) {
	const { actor, isAuthenticated, principal } = useAuth();

	return useQuery({
		queryKey: ["v2", "vault", vaultId, principal ?? "anon", isAuthenticated],
		queryFn: async (): Promise<UiVault | null> => {
			if (!vaultId || !hasProtocolEnv()) return null;
			const id = BigInt(vaultId);
			const a = actor ?? (await getV2ProtocolActor());
			if (!a) return null;

			// 1) Owner path — full vault when caller owns it
			if (isAuthenticated && actor) {
				try {
					const vaults = await getVaults(actor);
					const found = vaults.find((v) => v.id === id);
					if (found) {
						const [ui] = await enrichVaults([found]);
						if (ui) {
							return {
								...ui,
								isOwner: true,
							};
						}
					}
				} catch {
					/* fall through to public */
				}
			}

			// 2) Public explorer path — any vault
			if (typeof a.getPublicVault === "function") {
				try {
					const pub = await a.getPublicVault(id);
					const row = Array.isArray(pub) ? pub[0] : pub;
					if (row) {
						const collateral = (
							row.collateral as Array<[Record<string, null>, bigint]>
						).map(([ct, amt]) => {
							const k = Object.keys(ct)[0] ?? "ICP";
							const asset =
								k === "ckBTC" || k === "ckETH" || k === "ICP" ? k : "ICP";
							return [asset, Number(amt) / 1e8] as [string, number];
						});
						const collateralUsd = Number(row.collateralValueE8) / 1e8;
						const debtUsdm = Number(row.debtE8) / 1e8;
						const owner =
							typeof row.owner === "string" ? row.owner : null;
						const isOwner =
							!!principal && !!owner && principal === owner;
						return {
							id: row.id,
							collateralLabel:
								collateral.map(([k]) => k).join(" · ") || "Empty",
							collateralUsd,
							debtUsdm,
							ltvPct: Number(row.ltvBps) / 100,
							healthFactor: Number(row.healthFactorE8) / 1e8,
							status: "Active",
							source: "live",
							owner,
							createdNs: row.created ?? row.lastUpdated ?? null,
							isOwner,
							raw: {
								id: row.id,
								owner: owner as unknown as Vault["owner"],
								collateral: collateral as Vault["collateral"],
								debt: debtUsdm,
								created: row.created ?? 0n,
								lastUpdated: row.lastUpdated ?? 0n,
							},
						};
					}
				} catch {
					/* not found */
				}
			}
			return null;
		},
		enabled: Boolean(vaultId) && hasProtocolEnv(),
		staleTime: 15_000,
	});
}
